import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { getSnapshotDetails } from "../../../app/api/trendSnapshot";
import { RootState } from "../../../app/store";
import { ReactComponent as Minus } from "../../../assets/icons/minus.svg";
import { ReactComponent as Plus } from "../../../assets/icons/plus.svg";
import { BarData, DataNode } from "../../../interface/Trends";
import OverallAnalysisGraph from "../TrendsSidebar/OverallAnalysisGraph";
import ColorAnalysisSnapShot from "./ColorAnalysisSnapshot";
import {
  filterTrendCurveByDateRange,
  getFactoredTrendCurveResponse,
  shouldPreserveAllCurvePeriods,
} from "../../../utils/trendSidebarGraphUtils/trendCurveDataUtils";
import { TrendCurveChartData } from "../../../interface/DesignerInterface";
import TrendCurvesGraph from "../TrendsSidebar/TrendCurvesGraph";
import { filterArchivedGraphRows } from "../../../utils/archivedTrends";
import {
  getSnapshotTrendCurveWindowLabel,
  TREND_CURVE_LIVE_FILTER_TYPE,
} from "../../../utils/trendSidebarGraphUtils/trendCurvesUtils";
import { TrendCurveWindowType } from "../../../constants/constants";
import { checkSnapshotStatus } from "../../../utils/snapshotHelpers";

const GraphsSidebarForSnapshot = () => {
  const [colorAnalysisOpen, setColorAnalysisOpen] = React.useState(false);
  const [overallAnalysisOpen, setOverallAnalysisOpen] = React.useState(false);
  const [trendCurvesAnalysisOpen, setTrendCurvesAnalysisOpen] =
    React.useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [barData, setBarData] = React.useState<BarData[]>([]);
  const [l1AnalysisData, setL1AnalysisData] = React.useState<DataNode | null>(
    null
  );
  const [l2AnalysisData, setL2AnalysisData] = React.useState<DataNode | null>(
    null
  );

  const [trendsCurveData, setTrendsCurveData] = useState<TrendCurveChartData>({
    labels: [],
    datasets: [],
  });
  const [curveWindowType, setCurveWindowType] =
    useState<TrendCurveWindowType>("Month");
  const [snapshotStatus, setSnapshotStatus] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const user = useSelector((state: RootState) => state.user.userInfo);
  const { id } = useParams();
  const numericSnapshotId = Number(id);
  const fetchDetails = async (page: number) => {
    setLoading(true);
    const detailsParams = {
      page: page,
      numberOfTrends: 1,
      postsPerTrend: 1,
    };
    try {
      const snapDetails = await getSnapshotDetails(
        numericSnapshotId,
        user,
        detailsParams
      );
      const { status, isNotCompleted } = checkSnapshotStatus(snapDetails);
      setSnapshotStatus(status);
      if (isNotCompleted) {
        return;
      }
      // Set Bar Graph Data
      const trendCurveResponse = snapDetails?.data.trendCurveData;
      const genericFilters = snapDetails?.data?.snapshot?.genericFilters;
      const graphsFilters = genericFilters?.graphsFilters;
      const snapshotCurveWindow =
        getSnapshotTrendCurveWindowLabel(graphsFilters);
      setCurveWindowType(snapshotCurveWindow);

      const archivedTrendNames = genericFilters?.archivedTrendNames || [];
      const preserveAllPeriods = shouldPreserveAllCurvePeriods(
        genericFilters?.preferenceFilters
      );

      // Filter by date range from snapshot preferences
      const dateFilteredResponse = filterTrendCurveByDateRange(
        trendCurveResponse,
        genericFilters?.preferenceFilters
      );

      const factoredTrendCurveResponse = getFactoredTrendCurveResponse(
        dateFilteredResponse,
        snapshotCurveWindow,
        archivedTrendNames,
        preserveAllPeriods
      );
      setTrendsCurveData(factoredTrendCurveResponse);
      setBarData(
        filterArchivedGraphRows(
          snapDetails.data.barGraphData,
          archivedTrendNames
        )
      );

      //Set L1 Color Analysis Data
      if (snapDetails.data.l1ColorAnalysis) {
        const dataL1: DataNode = snapDetails.data.l1ColorAnalysis;

        // Set L2 Color Analysis Data
        const primary_color =
          snapDetails.data.snapshot?.genericFilters?.graphsFilters
            ?.primary_colour;

        if (snapDetails.data.l2ColorAnalysis && primary_color) {
          const dataL2: DataNode = snapDetails.data.l2ColorAnalysis;
          setL2AnalysisData(dataL2 as DataNode);
        }
        setL1AnalysisData(dataL1);
      }
    } catch (error) {
      console.error("Error fetching snapshot details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    fetchDetails(1);
  }, []);

  useEffect(() => {
    if (snapshotStatus === "pending" || snapshotStatus === "processing") {
      const interval = setInterval(() => {
        fetchDetails(1);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [snapshotStatus]);

  const toggleColorAnalysis = () => {
    setColorAnalysisOpen(prev => !prev);
  };

  const toggleOverallAnalysis = () => {
    setOverallAnalysisOpen(prev => !prev);
  };

  const toggleTrendCurveAnalysis = () => {
    setTrendCurvesAnalysisOpen(prev => !prev);
  };

  const isSnapshotProcessing =
    snapshotStatus === "pending" || snapshotStatus === "processing";
  const isTrendCurveLoading = loading || isSnapshotProcessing;

  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      {l1AnalysisData && (
        <div className="flex items-center justify-between px-1 w-full">
          <div className="flex">
            <div className="cursor-pointer" onClick={toggleColorAnalysis}>
              {colorAnalysisOpen ? <Minus /> : <Plus />}
            </div>
            <div className="transition-all duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-1 text-secondary">
              Color Analysis
            </div>
          </div>
          {colorAnalysisOpen && (
            <div
              className="right-2 text-right text-xs underline font-bold	mt-2 cursor-pointer"
              onClick={() =>
                navigate(`/trend-snapshot/details/color-analysis/${id}`)
              }
            >
              Expand
            </div>
          )}
        </div>
      )}
      {colorAnalysisOpen && (
        <div className="mx-3 w-[430px]">
          <ColorAnalysisSnapShot
            data1={l1AnalysisData}
            data2={l2AnalysisData}
          />
        </div>
      )}
      <div className="flex items-center justify-between px-1 w-full">
        <div className="flex">
          <div className="cursor-pointer" onClick={toggleOverallAnalysis}>
            {overallAnalysisOpen ? <Minus /> : <Plus />}
          </div>
          <div className="transition-all duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-1 text-secondary">
            Overall Analysis
          </div>
        </div>
        {overallAnalysisOpen && (
          <div
            className="right-2 text-right text-xs underline font-bold	mt-2 cursor-pointer"
            onClick={() =>
              navigate(`/trend-snapshot/details/overall-analysis/${id}`)
            }
          >
            Expand
          </div>
        )}
      </div>
      {overallAnalysisOpen && (
        <>
          <div className="mx-3 w-[430px]">
            <div className="relative overflow-x-scroll scrollbar-custom">
              <div className="" ref={scrollContainerRef}>
                <OverallAnalysisGraph barData={barData} />
              </div>
            </div>
          </div>
        </>
      )}
      <div className="flex items-center justify-between px-1 w-full">
        <div className="flex">
          <div className="cursor-pointer" onClick={toggleTrendCurveAnalysis}>
            {trendCurvesAnalysisOpen ? <Minus /> : <Plus />}
          </div>
          <div className="transition-all duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-1 text-secondary">
            Trend Curve
          </div>
        </div>
        {trendCurvesAnalysisOpen && (
          <div
            className="right-2 text-right text-xs underline font-bold	mt-2 cursor-pointer"
            onClick={() =>
              navigate(`/trend-snapshot/details/Trend-Curve-analysis/${id}`)
            }
          >
            Expand
          </div>
        )}
      </div>
      {trendCurvesAnalysisOpen && (
        <div className="mx-3 w-[430px]">
          <div className="text-sm mb-2">
            <span className="text-secondary">Filter:</span>{" "}
            {TREND_CURVE_LIVE_FILTER_TYPE}
            <span className="mx-2 text-secondary">|</span>
            <span className="text-secondary">Curve Window:</span>{" "}
            {isTrendCurveLoading ? "—" : curveWindowType}
          </div>
          {isTrendCurveLoading ? (
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
                {isSnapshotProcessing
                  ? "Snapshot is being generated…"
                  : "Loading trend curve…"}
              </div>
              <Skeleton height={280} className="w-full" />
            </div>
          ) : trendsCurveData.datasets.length > 0 ? (
            <TrendCurvesGraph
              trendCurveData={trendsCurveData}
              width={430}
              trendsCurveExpandedView={false}
              showLegend={false}
            />
          ) : (
            <div className="my-10 text-center">No Trend Curve Data</div>
          )}
        </div>
      )}
    </div>
  );
};

export default GraphsSidebarForSnapshot;
